// ============================================
// MATCH SCORE BADGE COMPONENT
// ============================================

import { cn } from '@/utils';
import { Sparkles, TrendingUp, Target } from 'lucide-react';
import { useTranslation } from '@crew-at-sea/i18n';

interface MatchScoreBadgeProps {
  score: number;
  showLabel?: boolean;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'badge' | 'card' | 'inline';
  className?: string;
}

export function MatchScoreBadge({ 
  score, 
  showLabel = true, 
  showIcon = false,
  size = 'md',
  variant = 'badge',
  className 
}: MatchScoreBadgeProps) {
  const { t } = useTranslation('panel');

  // Null/undefined/NaN score'ları yönet
  if (score === null || score === undefined || isNaN(score)) {
    return null;
  }
  
  const roundedScore = Math.round(score);
  
  // Puana göre renk belirle
  const getColorClasses = () => {
    if (roundedScore >= 80) {
      return {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-200',
        progress: 'bg-green-500',
        icon: 'text-green-600',
      };
    }
    if (roundedScore >= 60) {
      return {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        border: 'border-blue-200',
        progress: 'bg-blue-500',
        icon: 'text-blue-600',
      };
    }
    if (roundedScore >= 40) {
      return {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        border: 'border-yellow-200',
        progress: 'bg-yellow-500',
        icon: 'text-yellow-600',
      };
    }
    return {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      border: 'border-gray-200',
      progress: 'bg-gray-500',
      icon: 'text-gray-600',
    };
  };

  const colors = getColorClasses();

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'text-xs px-1.5 py-0.5';
      case 'lg':
        return 'text-base px-3 py-1.5';
      default:
        return 'text-sm px-2 py-1';
    }
  };

  const getMatchLabel = () => {
    if (roundedScore >= 80) return t('career.matchExcellent');
    if (roundedScore >= 60) return t('career.matchGood');
    if (roundedScore >= 40) return t('career.matchMedium');
    return t('career.matchLow');
  };

  const IconComponent = roundedScore >= 80 ? Sparkles : roundedScore >= 60 ? TrendingUp : Target;

  // Card variant - büyük görünüm, ilerleme çubuğu ile
  if (variant === 'card') {
    return (
      <div className={cn('p-4 rounded-lg border', colors.bg, colors.border, className)}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <IconComponent className={cn('w-5 h-5', colors.icon)} />
            <span className={cn('font-medium', colors.text)}>{getMatchLabel()}</span>
          </div>
          <span className={cn('text-2xl font-bold', colors.text)}>%{roundedScore}</span>
        </div>
        <div className="h-2 bg-white/50 rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-500', colors.progress)}
            style={{ width: `${Math.min(roundedScore, 100)}%` }}
          />
        </div>
      </div>
    );
  }

  // Inline variant - minimal görünüm
  if (variant === 'inline') {
    return (
      <span className={cn('inline-flex items-center gap-1', colors.text, className)}>
        {showIcon && <IconComponent className="w-3 h-3" />}
        <span className="font-medium">%{roundedScore}</span>
        {showLabel && <span className="text-gray-500">{t('career.matchLabel')}</span>}
      </span>
    );
  }

  // Varsayılan badge variant
  return (
    <div className={cn('inline-flex items-center gap-2', className)}>
      <span
        className={cn(
          'inline-flex items-center gap-1 font-semibold rounded-full border',
          colors.bg,
          colors.text,
          colors.border,
          getSizeClasses()
        )}
      >
        {showIcon && <IconComponent className={cn('w-3 h-3', size === 'lg' && 'w-4 h-4')} />}
        %{roundedScore}
        {showLabel && <span className="ml-0.5 font-normal">{t('career.matchLabel')}</span>}
      </span>
    </div>
  );
}

// Eşleşme detayları bileşeni
interface MatchDetailsProps {
  details: {
    rankMatch?: boolean;
    vesselTypeMatch?: boolean;
    experienceMatch?: boolean;
    certificateMatch?: number;
    salaryMatch?: boolean;
    reasons?: string[];
    missingRequirements?: string[];
  };
  className?: string;
}

export function MatchDetails({ details, className }: MatchDetailsProps) {
  const { t } = useTranslation('panel');

  const items = [
    { key: 'rankMatch', label: t('career.matchDetails.rank'), matched: details.rankMatch },
    { key: 'vesselTypeMatch', label: t('career.matchDetails.vesselType'), matched: details.vesselTypeMatch },
    { key: 'experienceMatch', label: t('career.matchDetails.experience'), matched: details.experienceMatch },
    { key: 'salaryMatch', label: t('career.matchDetails.salary'), matched: details.salaryMatch },
  ].filter(item => item.matched !== undefined);

  return (
    <div className={cn('space-y-3', className)}>
      {/* Eşleşme kriterleri */}
      <div className="grid grid-cols-2 gap-2">
        {items.map((item) => (
          <div
            key={item.key}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg text-sm',
              item.matched
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            )}
          >
            <span className={cn(
              'w-4 h-4 rounded-full flex items-center justify-center text-xs',
              item.matched ? 'bg-green-200' : 'bg-red-200'
            )}>
              {item.matched ? '✓' : '✗'}
            </span>
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      {/* Sertifika eşleşme yüzdesi */}
      {details.certificateMatch !== undefined && (
        <div className="flex items-center justify-between px-3 py-2 bg-blue-50 rounded-lg">
          <span className="text-sm text-blue-700">{t('career.matchDetails.certificateMatch')}</span>
          <span className="font-semibold text-blue-800">%{Math.round(details.certificateMatch)}</span>
        </div>
      )}

      {/* Eksik gereksinimler */}
      {details.missingRequirements && details.missingRequirements.length > 0 && (
        <div className="p-3 bg-yellow-50 rounded-lg">
          <p className="text-sm font-medium text-yellow-800 mb-2">{t('career.matchDetails.missingRequirements')}</p>
          <ul className="space-y-1">
            {details.missingRequirements.map((req, index) => (
              <li key={index} className="text-sm text-yellow-700 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                {req}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Eşleşme sebepleri */}
      {details.reasons && details.reasons.length > 0 && (
        <div className="p-3 bg-green-50 rounded-lg">
          <p className="text-sm font-medium text-green-800 mb-2">{t('career.matchDetails.matchReasons')}</p>
          <ul className="space-y-1">
            {details.reasons.map((reason, index) => (
              <li key={index} className="text-sm text-green-700 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                {reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
